import { Slot } from '@radix-ui/react-slot';
import type {
  TooltipArrowProps,
  TooltipContentProps,
  TooltipProps,
  TooltipProviderProps,
} from '@radix-ui/react-tooltip';
import * as Tooltip from '@radix-ui/react-tooltip';
import { type PropsWithChildren, type ReactNode, useState } from 'react';
import { styled } from 'styled-components';
import { FuuzeColors } from '../../styles/colors.js';

type FuuzeTooltipProps = {
  ProviderProps?: Partial<TooltipProviderProps>;
  RootProps?: TooltipProps;
  ContentProps?: TooltipContentProps;
  ArrowProps?: TooltipArrowProps;
  Content: ReactNode;
  showArrow?: boolean;
  underline?: boolean;
  disableHelpCursor?: boolean;
} & PropsWithChildren;

export function FuuzeTooltipOld(props: FuuzeTooltipProps) {
  const [open, setOpen] = useState(props.RootProps?.open || false);

  return (
    <Tooltip.Provider {...props.ProviderProps}>
      <Tooltip.Root
        {...props.RootProps}
        open={open}
        onOpenChange={open => {
          setOpen(open);
          props.RootProps?.onOpenChange?.(open);
        }}
        delayDuration={props.RootProps?.delayDuration}
      >
        <TriggerStyled $disableHelpCursor={!!props.disableHelpCursor} $underline={props.underline} asChild>
          <Slot
            onClick={_ => setOpen(true)}
            tabIndex={0}
            style={{ ...(!props.disableHelpCursor && { cursor: 'help' }) }}
          >
            {props.children}
          </Slot>
        </TriggerStyled>
        <Tooltip.Portal>
          <TooltipContentWrapper
            {...props.ContentProps}
            style={props.ContentProps?.style}
            sideOffset={props.ContentProps?.sideOffset || 5}
          >
            <>
              {props.Content}
              {props.showArrow && (
                <Tooltip.Arrow
                  style={{ fill: FuuzeColors.dark.dark900, ...props.ArrowProps?.style }}
                  {...props.ArrowProps}
                />
              )}
            </>
          </TooltipContentWrapper>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

const TooltipContentWrapper = styled(Tooltip.Content)`
  background-color: ${props => props.theme.colors.white};
  padding: 8px;
  border-radius: 8px;
  z-index: 11;
  color: ${props => props.theme.colors.dark.dark100};

  > * {
    color: ${props => props.theme.colors.dark.dark100};
  }
`;

const TriggerStyled = styled(Tooltip.Trigger)<{ $underline?: boolean; $disableHelpCursor: boolean }>`
  &:focus-visible {
    outline-offset: 0;
    outline-style: dashed;
    outline-width: 2px;
  };
  cursor: ${props => (props.$disableHelpCursor ? 'auto' : 'help')};
  text-decoration: ${props => (props.$underline ? `underline dashed ${props.theme.colors.dark.dark700}` : 'none')};
  text-underline-offset: 4px;
`;
